// Menu fonts. Each option prints with a fallback stack, so an office that
// does not have a font installed gets a similar served font instead of the
// browser default (Times New Roman), in the preview and in the PDF.
//
// "localOnly" fonts are not served by this app: they are used only if the
// office's own computer has them (Cooper Black ships with Microsoft Office,
// Futura and Chalkboard SE with macOS). See README "Fonts and licenses".

export interface MenuFont {
  label: string;
  value: string;
  stack: string;
  localOnly?: boolean;
}

export const MENU_FONTS: MenuFont[] = [
  { label: 'Cooper Black', value: 'Cooper Black', stack: "'Cooper Black', 'Paytone One', sans-serif", localOnly: true },
  { label: 'Futura Bold', value: 'Futura-Bold', stack: "'Futura-Bold', 'Futura', 'Poppins', sans-serif", localOnly: true },
  { label: 'Helvetica Now', value: 'Helvetica Now', stack: "'Helvetica Now', 'Helvetica Neue', Arial, sans-serif" },
  { label: 'Paytone One', value: 'Paytone One', stack: "'Paytone One', sans-serif" },
  { label: 'Fredoka One', value: 'Fredoka One', stack: "'Fredoka One', sans-serif" },
  {
    label: 'Chalkboard SE Bold',
    value: 'Chalkboard SE Bold',
    stack: "'Chalkboard SE Bold', 'Chalkboard SE', 'Bubblegum Sans', cursive",
    localOnly: true,
  },
  { label: 'Lilita One', value: 'Lilita One', stack: "'Lilita One', sans-serif" },
  { label: 'Luckiest Guy', value: 'Luckiest Guy', stack: "'Luckiest Guy', sans-serif" },
  { label: 'Bubblegum Sans', value: 'Bubblegum Sans', stack: "'Bubblegum Sans', cursive" },
];

/** The designed default. Used when the office's computer has it. */
export const DEFAULT_FONT = 'Cooper Black';
/** Closest served font, used as the default when Cooper Black is missing. */
export const SERVED_DEFAULT_FONT = 'Paytone One';

export const fontStack = (value: string): string => {
  const known = MENU_FONTS.find(font => font.value === value);
  if (known) return known.stack;
  return `'${value.replace(/['\\]/g, '')}', sans-serif`;
};

/** True when the browser can actually use `family` (a local() face that resolved). */
export const isFontAvailable = async (family: string): Promise<boolean> => {
  try {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts || typeof fonts.load !== 'function') return false;
    const faces = await fonts.load(`bold 48px "${family}"`);
    return faces.some(face => face.status === 'loaded');
  } catch {
    return false;
  }
};
